import * as THREE from 'three';
import 'webvr-polyfill';
import '../utils/viveController';
import '../utils/objLoader';
import '../utils/reticle';
import '../utils/oculusController';
import '../utils/daydreamController';

import { disableDeviceTracking, enableDeviceTracking,
  getCamera, getPrevCamera, isVRMode, RENDER_MODES,
  setRenderMode, useCamera, useVrCamera, } from '../../modules/player';
import { MOUSE_CONTROLS, updateCommands, updateCommand, activateCommand, deactivateCommand, setCommandOptions, getCommands, isCommandActive } from '../../modules/commands';
import decompose from '../utils/decompose';
import VRControls from '../utils/vrControls'; // extend three with VRControls
import VREffect from '../utils/vrEffect';
import getTargetWorldMatrix from '../utils/getTargetWorldMatrix';
import updateCameraLookAtTarget from '../utils/updateCameraLookAtTarget';
import { merge, vrCameraId, get, virtualTargetId } from '../../modules/sceneGraph';
import { get as sceneGet, set as sceneSet, getWorldTransform } from '../../modules/scene';

export default function (store, translator) {
  let vrEffect, vrDisplay, vrReticle, vrFloor, vrGrid, vrControls, hasExternalDisplay,
    controller1, controller2, oculusController, daydreamController, controllerHandler,
    mobileNavHandler, threeVrCamera, firstPersonActive, prevMoveMode, controllersAdded = false,
    prevHandlers = false, prevKeyboard = false;

  const scene = translator.scene;
  const camera = translator.camera;

  let vrCapable = false;
  if (__BROWSER__) {
    window.navigator.getVRDisplays().then((displays)=> {
      if (!displays.length) return;
      vrCapable = displays[0].capabilities.canPresent;
      hasExternalDisplay = displays[0].capabilities.hasExternalDisplay;
      store.dispatch(updateCommand('vrMode', { enabled: vrCapable }))
      vrDisplay = displays[0];
    });
  }

  function controllerNavHandle(manipulatorsObject) {

    return (ev) => {
      if (!vrReticle.armObject) return;

      var pos = vrReticle.getReticlePosition().clone();
      pos.y = 0;
      var vrdpPos = threeVrCamera.position.clone();
      var offset = pos.addScaledVector(vrControls.offset, 2).sub(vrdpPos);
      offset.y = 0;
      vrControls.setOffset(offset);
    };
  }

  function navHandle(manipulatorsObject) {
    return (ev) => {
      if (!vrReticle.armObject) return;

      const cameraId = getCamera(store);
      var pos = vrReticle.getReticlePosition().clone();
      pos.y = 0;
      manipulatorsObject.position.add(pos);
      manipulatorsObject.updateMatrix();

      const newCameraPosition = manipulatorsObject.position.clone();
      vrControls.setOffset(newCameraPosition);
      //store.dispatch(merge([cameraId, 'Transform', 0], decompose(newCameraPosition)));
    }
  }

  function addControllers(manipulatorsObject){
    var loader = new THREE.OBJLoader();
    manipulatorsObject.add(controller1);
    manipulatorsObject.add(controller2);
    loader.setPath( 'models/' );
    loader.load('vr_controller_vive_1_5.obj', (object) => {
      var loader = new THREE.TextureLoader();
      loader.setPath('models/');

      var controller = object.children[0];
      var material = new THREE.MeshBasicMaterial();
      material.map = loader.load('onepointfive_texture.png');
      material.specularMap = loader.load('onepointfive_spec.png');
      controller.material = material;

      controller1.add(object.clone());
      controller2.add(object.clone());
    });
    controllersAdded = true;
  }

  function addHandlers(manipObject){
    mobileNavHandler = navHandle(manipObject);
    window.addEventListener('touchend', mobileNavHandler);

    controllerHandler = controllerNavHandle(manipObject);
    window.addEventListener("thumbpadup", controllerHandler);
  }

  function removeHandlers(){
    window.removeEventListener('touchend', mobileNavHandler);
    window.removeEventListener("thumbpadup", controllerHandler);
    mobileNavHandler = null;
    controllerHandler = null;
  }

  function enableFirstPerson(){
    store.dispatch(activateCommand('firstPersonMode'));
    store.dispatch(setCommandOptions('firstPersonMode', {movementMode: 'plane'}));
  }

  function disableFirstPerson(){
    store.dispatch(deactivateCommand('firstPersonMode'));
    store.dispatch(setCommandOptions('firstPersonMode', {movementMode: prevMoveMode}));
  }

  return {
    label: 'VR mode',
    activeLabel: 'Exit VR Mode',

    enabled: vrCapable,
    display: {
      playerTools: true,
    },
    options: {
      planeEnabled: false,
      navigationEnabled: true,
      keyboardEnabled: true,
    },

    isActive: isVRMode,

    widget: {
      draw: function (manipulatorsObject) {
        const cbgeometry = new THREE.PlaneGeometry(20, 20, 1, 1);
        const mat = new THREE.MeshBasicMaterial({ color: 0x666666 });
        mat.transparent = true;//!this.options.planeEnabled;
        mat.opacity = this.options.planeEnabled ? 1.0 : 0.0;
        vrFloor = new THREE.Mesh(cbgeometry, mat);
        vrFloor.rotation.x = -Math.PI * 0.5;

        vrReticle = new THREE.Reticle(camera);
        vrReticle.addCollider(vrFloor);

        if(this.options.navigationEnabled) manipulatorsObject.add(vrReticle.armObject);
        manipulatorsObject.add(vrReticle.dot);
        manipulatorsObject.add(vrFloor);

        // This floor grid is __UGLY__!
        //vrGrid = floorGrid();
        //manipulatorsObject.add(vrGrid);

        if(hasExternalDisplay) {
          //------Add vive controllers-----------
          if(controller1 && controller2 && !controllersAdded) addControllers(manipulatorsObject);
        }

        prevHandlers = this.options.navigationEnabled;
        if(this.options.navigationEnabled) addHandlers(manipulatorsObject);
      },

      remove: function(manipulatorsObject) {
        if(this.options.navigationEnabled) removeHandlers();
      },

      position: function(manipulatorsObject) {
        if(prevHandlers!==this.options.navigationEnabled){
          this.options.navigationEnabled ? addHandlers(manipulatorsObject) : removeHandlers(manipulatorsObject);
          manipulatorsObject[this.options.navigationEnabled ? 'add' : 'remove'](vrReticle.armObject);
          prevHandlers = this.options.navigationEnabled;
        }

        if(prevKeyboard!==this.options.keyboardEnabled){
          this.options.keyboardEnabled ? enableFirstPerson() : disableFirstPerson();
          prevKeyboard = this.options.keyboardEnabled;
        }

        if(vrControls) {
          vrControls.update();
          manipulatorsObject.position.copy(vrControls.offset);
        }

        if (threeVrCamera) {
          threeVrCamera.updateMatrix();
          matchToCamera(store, threeVrCamera);
        }

        vrReticle.update(manipulatorsObject);

        let controllerFound = !hasExternalDisplay

        if(controller1 && controller2 && !controllersAdded) addControllers(manipulatorsObject);

        if (hasExternalDisplay && controller1 && controller2) {
          controller1.update();
          controller2.update();
          controller1.position.applyQuaternion(vrControls.rotationalOffset);
          controller2.position.applyQuaternion(vrControls.rotationalOffset);
          controller1.quaternion.premultiply(vrControls.rotationalOffset);
          controller2.quaternion.premultiply(vrControls.rotationalOffset);
          controller1.updateMatrix();
          controller2.updateMatrix();
          controllerFound = controllerFound || !!controller1.getGamepad();
        }

        if (hasExternalDisplay && oculusController){
          oculusController.update();
          controllerFound = controllerFound || !!oculusController.getGamepad();
        }

        if (daydreamController){
          daydreamController.update();
          controllerFound = controllerFound || !!daydreamController.getGamepad();
        }

        if(controllerFound) manipulatorsObject.add(vrReticle.dot);
        else manipulatorsObject.remove(vrReticle.dot);

        const downVector = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        const clampedY = Math.min(downVector.y, 0);
        const opacity = 1-Math.sqrt(1-clampedY*clampedY);
        if(this.options.planeEnabled) vrFloor.material.opacity = opacity;
        //vrGrid.material.opacity = opacity;
        var roundX = Math.round(2*manipulatorsObject.position.x)/2 - manipulatorsObject.position.x;
        var roundZ = Math.round(2*manipulatorsObject.position.z)/2 - manipulatorsObject.position.z;
        //vrGrid.position.set(roundX, 0 ,roundZ);
        manipulatorsObject.updateMatrix();
      },
    },

    activate: function () {
      const translator = store.getTranslator();
      const scope = this;
      firstPersonActive = isCommandActive(store, 'firstPersonMode');
      if (vrDisplay) {
        vrEffect = new VREffect(translator.renderer, vrDisplay);
        translator.externals.vrEffect = vrEffect;

        //hasExternalDisplay = vrDisplay.capabilities.hasExternalDisplay;
        if(!vrDisplay.capabilities.canPresent) return;

        const cameraId = getCamera(store);
        const cameraPosition = new THREE.Vector3().setFromMatrixPosition(
          getWorldTransform(store, cameraId)
        );
        cameraPosition.y=0;

        const targetPosition = new THREE.Vector3().setFromMatrixPosition(
          getTargetWorldMatrix(store, cameraId)
        ).sub(cameraPosition)
        targetPosition.y=0;
        targetPosition.normalize();
        const lookDirection = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0,0,-1), targetPosition);

        threeVrCamera = translator.camera.clone();

        vrControls = new VRControls(threeVrCamera);

        store.dispatch(useVrCamera(true));

        return vrEffect.requestPresent().then(() => {
          prevMoveMode = getCommands(store).firstPersonMode.options.movementMode;
          if(scope.options.keyboardEnabled) enableFirstPerson();
          else disableFirstPerson();
          prevKeyboard = scope.options.keyboardEnabled;

          vrControls.setOffset(cameraPosition);
          vrControls.setRotationalOffset(lookDirection);

          translator.externals.vrControls = vrControls;
          vrControls.standing = true;
          vrControls.resetPose(); // zero orientation controls

          vrControls.update();
          threeVrCamera.updateMatrix();
          matchToCamera(store, threeVrCamera);

          if(hasExternalDisplay){
            controller1 = new THREE.ViveController( 0 );
            controller1.standingMatrix = vrControls.getStandingMatrix();

            controller2 = new THREE.ViveController( 1 );
            controller2.standingMatrix = vrControls.getStandingMatrix();

            oculusController = new THREE.OculusController( 0 );

          } else {
            daydreamController = new THREE.DaydreamController();
          }


          store.dispatch(setRenderMode(RENDER_MODES.vr));
          store.dispatch(updateCommands(MOUSE_CONTROLS, { enabled: false }));

          store.setRequestAnimationFrame(vrDisplay.requestAnimationFrame, vrDisplay);

          return vrDisplay;
        }).catch((err) => {
          console.log('Error Activating VR');
          throw err;
        });
      }
    },

    deactivate: function () {
      const translator = store.getTranslator();

      if(vrDisplay.isPresenting) vrEffect.exitPresent();
      store.setRequestAnimationFrame();

      if(!firstPersonActive) disableFirstPerson();

      store.dispatch(setRenderMode(RENDER_MODES.normal));
      store.dispatch(updateCommands(MOUSE_CONTROLS, { enabled: true }));
      store.dispatch(useVrCamera(false));

      //vrDisplay = null;
      threeVrCamera = null;
      vrControls = null;

      /*const prevCameraId = getPrevCamera(store);
      if (prevCameraId) store.dispatch(useCamera(prevCameraId));*/

      if (!hasExternalDisplay) {
        //scene.remove(camera);
      } else {
        controller1 = null;
        controller2 = null;
        oculusController = null;
        daydreamController = null;
        controllersAdded = false;
        //scene.position.set(0, 0, 0);
        //vrElements.position.set(0, 0, 0);
      }

      //vrControls.standing = false;
      vrReticle.removeCollider(vrFloor);
      translator.renderer.setScissorTest(false);
      //vrElements.children = [];
      //scene.remove(vrElements);
    },
  };
}

function floorGrid() {
  // Materials
  var mat = new THREE.LineBasicMaterial( {color: 0x00ffff, linewidth: 1, depthTest:false, depthWrite:false} );
  mat.transparent = false;

  let lineGeometry = new THREE.BufferGeometry();
  let positions = new Float32Array(40 * 2 * 3);

  var i = 0;
  for (let j = 0; j < 40; j ++ ) {

    var x = j * 0.5 - 10;

    positions[i * 6 + 0] = x;
    positions[i * 6 + 1] = 0;
    positions[i * 6 + 2] = -10;
    positions[i * 6 + 3 + 0] = x;
    positions[i * 6 + 3 + 1] = 0;
    positions[i * 6 + 3 + 2] = 10;

    i++;
  }

  for (let z = -10, i = 40; z < 10; z += 0.5, i++) {
    /*positions[i * 6 + 0] = -10;
    positions[i * 6 + 1] = 0;
    positions[i * 6 + 2] = z;
    positions[i * 6 + 3 + 0] = 10;
    positions[i * 6 + 3 + 1] = 0;
    positions[i * 6 + 3 + 2] = z;*/
  }

  lineGeometry.addAttribute('position', new THREE.BufferAttribute(positions, 3));

  let mesh = new THREE.LineSegments(lineGeometry, mat);
  mesh.mode = THREE.LineSegments;
  //mesh.position.set(0,0.1,0);

  return mesh;
}

function matchToCamera(store, threeCamera) {
  const cameraId = getCamera(store);
  const targetWorldMatrix = getTargetWorldMatrix(store);
  const targetPosition = new THREE.Vector3().setFromMatrixPosition(targetWorldMatrix);

  const length = targetPosition.sub(threeCamera.position).length();
  targetPosition.set(0, 0, -length).applyQuaternion(threeCamera.quaternion).add(threeCamera.position);
  const upPosition = new THREE.Vector3(0, 1, 0).applyQuaternion(threeCamera.quaternion);

  targetWorldMatrix.setPosition(targetPosition);
  const camData = sceneGet(store, { id: cameraId, evalPlug: 'Camera' });
  camData.upPosition = upPosition;
  store.dispatch(merge([cameraId, 'Transform', 0], decompose(threeCamera.matrix.clone())));
  if (!sceneGet(store, { id: cameraId, evalPlug: 'Transform' }).target) {
    store.dispatch(merge([virtualTargetId, 'Transform', 0], decompose(targetWorldMatrix)));
  }
}



// WEBPACK FOOTER //
// ./src/translators/tools/vrMode.js